import type { AccountBucket, CostBucket, CostsState } from '@orgus/protocol';
import { FolderGit2, Sparkles, WalletCards } from '@orgus/ui/icons';
import { BarList, DATA_SLOT_BG, EmptyState, Panel, Tooltip, cn, formatMoney, formatPercent } from '@orgus/ui';
import type { DataSlot } from '@orgus/ui';
import { shares, topWithOther } from './model.ts';
import { AccountGlyph, ApiKeyHint, accountMeta } from './accounts.tsx';

const OTHER = '__other';

/** Spend this month per project, most expensive first: a ranked bar list with each share. */
export function ProjectBreakdown({ buckets, className }: { buckets: CostBucket[]; className?: string }) {
  const rows = topWithOther(buckets, 7);
  const total = rows.reduce((n, b) => n + b.cost, 0);
  return (
    <Panel title="By project" sub={rows.length ? `${formatMoney(total)} this month` : undefined} icon={<FolderGit2 />} className={className}>
      {rows.length === 0 ? (
        <EmptyState title="No project spend yet" description="Each project's share appears once its sessions use tokens this month." />
      ) : (
        <BarList
          label="Spend by project, this month"
          items={rows.map((b) => ({
            key: b.key || 'none',
            label: b.label,
            value: b.cost,
            other: b.key === OTHER,
            lead: (
              <span aria-hidden className="flex size-5 items-center justify-center rounded-control bg-active text-caption font-medium text-fg-muted">
                {b.label.slice(0, 1).toUpperCase()}
              </span>
            ),
          }))}
          format={formatMoney}
          shares
        />
      )}
    </Panel>
  );
}

/**
 * One account's label in the by-account list: the name wins the width. The dim note takes its
 * short form in a narrow list and is dropped in a very narrow one (container queries on the
 * list); the whole label and note show on hover.
 */
function AccountLabel({ b }: { b: AccountBucket }) {
  const meta = accountMeta(b);
  // A bare signed amount says nothing: name what it is.
  const short = b.key && b.unmatched ? `${formatMoney(b.unmatched)} unmatched` : meta.short;
  return (
    <Tooltip content={`${b.label}: ${meta.hint ?? meta.text}`}>
      <span tabIndex={-1} className="flex min-w-0 items-baseline gap-2">
        <span className="min-w-0 truncate">{b.label}</span>
        <span className="hidden shrink-0 text-caption text-fg-subtle @xs:inline">
          <span className="hidden @sm:inline">{meta.text}</span>
          <span className="@sm:hidden">{short}</span>
        </span>
      </span>
    </Tooltip>
  );
}

/** Spend this month per account or provider (ADR 0067), with what its figure means: notional or list price. */
export function AccountBreakdown({ buckets, costs, className }: { buckets: AccountBucket[]; costs?: CostsState; className?: string }) {
  const total = buckets.reduce((n, b) => n + b.cost, 0);
  return (
    <Panel title="By account" sub={buckets.length ? `${formatMoney(total)} this month` : undefined} icon={<WalletCards />} className={className}>
      {buckets.length === 0 ? (
        <EmptyState title="No account spend yet" description="Each account's share appears once its sessions use tokens this month." />
      ) : (
        <div className="@container flex min-w-0 flex-col gap-4">
          <BarList
            label="Spend by account, this month"
            items={buckets.slice(0, 7).map((b) => ({
              key: b.key || 'unknown',
              label: <AccountLabel b={b} />,
              value: b.cost,
              other: !b.key,
              lead: <AccountGlyph kind={b.kind} />,
            }))}
            format={formatMoney}
            shares
          />
          <ApiKeyHint costs={costs} />
        </div>
      )}
    </Panel>
  );
}

/** Which models the money went to: one share bar for the whole, then each model ranked, in the same colours. */
export function ModelMix({ buckets, className }: { buckets: CostBucket[]; className?: string }) {
  const rows = shares(topWithOther(buckets, 5));
  const slot = (i: number) => (Math.min(6, i + 1) as DataSlot);
  const total = rows.reduce((n, b) => n + b.cost, 0);
  return (
    <Panel title="By model" sub={rows.length ? `${formatMoney(total)} this month` : undefined} icon={<Sparkles />} className={className}>
      {rows.length === 0 ? (
        <EmptyState title="No model spend yet" description="Spend per model appears once sessions use tokens this month." />
      ) : (
        <div className="flex flex-col gap-6">
          <div role="img" aria-label={`Model mix: ${rows.map((r) => `${r.label} ${formatPercent(r.share)}`).join(', ')}`} className="motion-grow-x flex h-2 gap-1 overflow-hidden rounded-pill">
            {rows.map((r, i) => (
              <Tooltip key={r.key} content={`${r.label}: ${formatMoney(r.cost)}, ${formatPercent(r.share)}`}>
                <span tabIndex={-1} className={cn('h-full min-w-1 first:rounded-l-pill last:rounded-r-pill', r.key === OTHER ? 'bg-fg-subtle' : DATA_SLOT_BG[slot(i)])} style={{ flexGrow: r.cost }} />
              </Tooltip>
            ))}
          </div>
          <BarList
            label="Spend by model, this month"
            items={rows.map((r) => ({ key: r.key, label: r.label, value: r.cost, other: r.key === OTHER }))}
            slotOf={(_, i) => slot(i)}
            format={formatMoney}
            shares
          />
        </div>
      )}
    </Panel>
  );
}
